import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ObjectSchema } from 'yup';
import { captureFormValidationError } from '../../../util/SentryUtils';
import { ConfigStatus } from '../../AdminPanel/DataDisplay/useDisplayConfigState';
import { ArkPesFormValues, arkPesConfigSchema, getDefaultArkPesConfig } from './arkPesConfig.schema';
import { useArkPesConfigActions } from './useArkPesConfigActions';
import { trackEvent } from '../../../util/datadogTracking';
import { AppName } from '../../../data-models/integrations-app.data-model';

export function useArkPesConfigFormState() {
  const [editStatus, setEditStatus] = useState<ConfigStatus>('initial');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { config, isConnected, isLoading, saveConfig, resetConfig } = useArkPesConfigActions();

  const schema = arkPesConfigSchema();
  const methods = useForm<ArkPesFormValues>({
    resolver: yupResolver(schema as ObjectSchema<ArkPesFormValues>),
    defaultValues: getDefaultArkPesConfig(),
    mode: 'all',
  });

  const {
    getValues,
    formState: { errors, isDirty },
    reset,
    trigger,
  } = methods;

  useEffect(() => {
    if (config?.config) {
      reset({
        clientId: config.config.clientId || '',
      });
    }
  }, [config, reset]);

  useEffect(() => {
    if (isDirty && (editStatus === 'initial' || editStatus === 'saved')) {
      setEditStatus('modified');
    }
  }, [isDirty, editStatus]);

  const onSave = useCallback(async () => {
    const isValid = await trigger();
    if (!isValid) {
      captureFormValidationError('Ark PES Config', errors);
      return;
    }
    try {
      const result = await saveConfig(getValues());
      setSuccessMessage(result.message);
      setErrorMessage('');
      setEditStatus('saved');
      reset(getValues());
      trackEvent('integration.connected', { provider: AppName.ArkPes, success: true });
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      setErrorMessage(axiosError?.response?.data?.message || 'Failed to configure Ark PES connection');
      setSuccessMessage('');
      trackEvent('integration.connected', { provider: AppName.ArkPes, success: false });
    }
  }, [trigger, saveConfig, getValues, errors, reset]);

  const onReset = useCallback(async () => {
    await resetConfig();
    setEditStatus('initial');
    setSuccessMessage('');
    setErrorMessage('');
  }, [resetConfig]);

  return {
    editStatus,
    isConnected,
    isLoading,
    successMessage,
    errorMessage,
    onSave,
    onReset,
    methods,
  };
}
